/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
// TODO: Fix TypeScript any types - properly type API response interfaces
import { apiClient } from '@/lib/api-client';
import type { LatLngBounds, LatLng } from '@/types/global';
import type { ChargePointFilterParams } from '@/features/filters/types/filter.types';
import type {
  ChargePointsMapResponse,
  ChargePointDetailsResponse,
  ChargePointGroupResponse,
  ChargePointData,
  ChargePointWrapper,
  ChargePointCluster,
  ChargePointState,
  PricingCollection,
  Pricing,
  PricingEntry,
  PricingDynamicPriceEntry,
} from '../types/charge-point.types';

import { useClusterTuningStore } from '@/features/markers/store/cluster-tuning-store';

/** The backend clusters while the requested zoom is below this */
const BACKEND_CLUSTER_ZOOM = 14;

/**
 * Preview (new cluster styles): leave clusters as soon as few enough
 * chargers are in view, instead of only at map zoom 15.
 * ?cluster_style=ring keeps today's behaviour.
 */
function breakOutEarly(urlParams: URLSearchParams): boolean {
  return urlParams.get('cluster_style') !== 'ring';
}

/**
 * Backend address fields sometimes hold the literal string "null" (bad data),
 * which would otherwise render as e.g. "null København".
 */
export function cleanText(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed && trimmed.toLowerCase() !== 'null' ? trimmed : undefined;
}

/**
 * Parse location string "lat,lng" to coordinate object
 */
function parseLocation(location: string): LatLng {
  if (!location) {
    console.warn('parseLocation called with undefined/null location');
    return { lat: 0, lng: 0 };
  }
  const parts = location.split(',').map(Number);
  const lat = parts[0] ?? 0;
  const lng = parts[1] ?? 0;
  return { lat, lng };
}

/**
 * Transform pricing entry from API response
 */
function transformPricingEntry(item: any): PricingEntry {
  return {
    id: item.id,
    amount: parseFloat(item.amount),
    type: item.type,
    label: item.label,
    description: item.description,
    startMinAfterCharging: item.start_min_after_charging,
    startMinAfterFullyCharged: item.start_min_after_fully_charged,
    isMasterPrice: item.master ?? false,
    endAtFullyCharged: item.end_at_fully_charged,
    additionalFeeType: item.additional_fee_type,
    amountMin:
      item.amount_min != null ? parseFloat(item.amount_min) : undefined,
    amountMax:
      item.amount_max != null ? parseFloat(item.amount_max) : undefined,
  };
}

/**
 * Transform pricing data from API response
 */
function transformPricing(item: any): Pricing | undefined {
  if (!item) return undefined;

  // Parse dynamic price entries from chart data
  const dynamicPriceEntries: PricingDynamicPriceEntry[] = [];
  if (item.chart?.sets?.[0]?.values) {
    item.chart.sets[0].values.forEach((entry: any) => {
      if (entry.x && entry.y !== undefined) {
        dynamicPriceEntries.push({
          time: new Date(entry.x).toISOString(), // Ensure it's ISO format
          value: parseFloat(entry.y),
        });
      }
    });
  }

  // Parse pricing entries
  const pricingEntries: PricingEntry[] = (item.pricing_entries || []).map(
    transformPricingEntry
  );

  return {
    label: item.label || '',
    reservationPerMin: item.reservation_per_min
      ? parseFloat(item.reservation_per_min)
      : undefined,
    currency: item.currency || { identifier: 'DKK' },
    pricingEntries,
    masterPricing: item.master_pricing
      ? transformPricingEntry(item.master_pricing)
      : undefined,
    dynamicPriceEntries:
      dynamicPriceEntries.length > 0 ? dynamicPriceEntries : undefined,
    dynamic: item.dynamic ?? false,
  };
}

/**
 * Transform pricing collection from API response
 */
function transformPricingCollection(item: any): PricingCollection | undefined {
  if (!item) return undefined;

  return {
    publicLabel: item.public_label,
    userLabel: item.user_label,
    pricingsPublic: transformPricing(item.public),
    pricingsUser: transformPricing(item.user),
    pricingsTeam: transformPricing(item.team),
    pricingsTeamMember: transformPricing(item.team_member),
  };
}

/**
 * Transform API response to expected format
 */
function transformChargePointWrapper(item: any): ChargePointWrapper {
  const location = parseLocation(item.location);
  const isChargePointGroup = item.document === 'charge_point_group';

  // Prefer the explicit API `state` field; the pin color is not a reliable proxy for
  // status (busy points use grey, passive uses blue, disconnected/error/maintenance all
  // use red). Fall back to pin-color inference only when the state is missing/unknown.
  const pinColor = item.pin?.color || 'gray';
  const chargePointState =
    mapApiStateToChargePointState(item.state ?? item.charge_point_state) ??
    inferStateFromPinColor(pinColor);

  return {
    id: isChargePointGroup ? `group-${item.id}` : `cp-${item.id}`,
    type: isChargePointGroup ? 'chargePointGroup' : 'chargePoint',
    coordinate: location,
    pin: {
      color: pinColor,
      type: item.pin?.type || 'single',
      number: item.pin?.number || 1,
      colors: item.pin?.colors,
    },
    ...(isChargePointGroup
      ? {
          chargePointGroup: {
            id: item.id,
            name: item.name,
            coordinate: location,
            pin: item.pin,
            address1: cleanText(item.address1),
            city: cleanText(item.city),
            operatorName: item.operator_name,
            operator_id: item.operator_id,
            availableCount: item.charge_point_available_count,
            chargePointCount: item.charge_point_count,
            maxKwLabel: item.max_kw_label,
            typeLabel: item.type_label,
            priceLabel: item.price_label, // Price label for Best Price feature
            connectors: item.connectors || [],
            currency: item.currency || { identifier: 'EUR' },
            promotionCodeIds: item.promotion_code_ids,
          },
        }
      : {
          chargePoint: {
            id: item.id,
            identifier: item.identifier,
            name: item.name,
            coordinate: location,
            address1: cleanText(item.address1),
            address2: cleanText(item.address2),
            address3: cleanText(item.address3),
            city: cleanText(item.city),
            zip: cleanText(item.zip),
            maxKw: item.max_kw,
            maxKwLabel: item.max_kw_label,
            typeLabel: item.type_label,
            available: item.charge_point_available_count > 0,
            chargePointState,
            connectors: item.connectors || [],
            currency: item.currency || { identifier: 'EUR' },
            operatorName: item.operator_name,
            operator_id: item.operator_id,
            pricingCollection: transformPricingCollection(
              item.pricings || item.pricing_collection
            ),
            priceLabel: item.price_label, // Direct price label from map API (e.g., "4.24 DKK/kWh")
            pin: item.pin,
            promotionCodeIds: item.promotion_code_ids,
          },
        }),
  };
}

/**
 * Transform cluster from API response
 * Note: Clusters have lat/lng as separate properties, not as a "location" string
 */
function transformCluster(item: any): ChargePointCluster {
  // Clusters use separate lat/lng properties
  const location: LatLng = {
    lat: item.lat || 0,
    lng: item.lng || 0,
  };

  // Use the pin type from API if available (supports 'segmented' type)
  // Fall back to 'cluster' for backwards compatibility
  const pinType = item.pin?.type || 'cluster';

  return {
    id: `cluster-${item.lat}-${item.lng}`,
    count: item.count || item.pin?.number || 1,
    coordinate: location,
    pin: {
      color: item.pin?.color || 'gray',
      type: pinType,
      number: item.pin?.number || item.count || 1,
      colors: item.pin?.colors,
    },
  };
}

/**
 * Map the API `state` string to the UI `ChargePointState` union.
 *
 * The API exposes the real status in the `state` field (e.g. "available", "busy",
 * "busy-charging", "busy-blocked", "disconnected", "passive", "reserved", "error",
 * "maintenance"). The pin color is NOT a reliable proxy for status — busy points use a
 * grey pin, passive uses blue, and disconnected/error/maintenance all use red — so we
 * must map the explicit state instead of inferring it from the pin.
 *
 * Returns `undefined` for missing/unknown states so callers can fall back to pin-color
 * inference.
 */
export function mapApiStateToChargePointState(
  rawState?: string | null
): ChargePointState | undefined {
  if (!rawState) return undefined;
  const state = String(rawState).toLowerCase();

  if (state === 'available') return 'available';
  // Reservation can surface as "reserved" or as a "busy-reserved" sub-state.
  if (state.includes('reserved')) return 'reserved';
  // Every busy sub-state (busy, busy-charging, busy-blocked, busy-scheduled, ...) is "busy".
  if (state.startsWith('busy')) return 'busy';
  if (state === 'passive') return 'passive';
  if (state === 'disconnected' || state === 'offline') return 'disconnected';
  if (state === 'error' || state === 'maintenance') return 'error';

  return undefined;
}

/**
 * Infer charge point state from pin color. Only used as a last resort when the API does
 * not provide a usable `state` field. Note that pin colors are reused across states
 * (busy is grey, passive is blue, disconnected/error/maintenance are red), so this is
 * inherently lossy — prefer `mapApiStateToChargePointState`.
 */
function inferStateFromPinColor(pinColor: string): ChargePointState {
  switch (pinColor) {
    case 'green':
      return 'available';
    case 'orange':
      return 'busy';
    case 'red':
      return 'error';
    default:
      return 'passive';
  }
}

/**
 * Transform charge point group from API response (snake_case to camelCase)
 */
function transformChargePointGroup(item: any): any {
  // Parse theme data from theme property (available in group details API)
  const theme = item.theme
    ? {
        id: item.theme.id,
        name: item.theme.name,
        primaryColor: item.theme.primary_color,
        secondaryColor: item.theme.secondary_color,
        logoUrl: item.theme.logo_url,
      }
    : undefined;

  return {
    id: item.id,
    name: item.name,
    coordinate: parseLocation(
      item.location || `${item.lat || 0},${item.lng || 0}`
    ),
    pin: item.pin || {
      color: 'gray',
      type: 'group',
      number: item.charge_point_count || 1,
      colors: undefined,
    },
    address1: cleanText(item.address1),
    city: cleanText(item.city),
    operatorName: item.operator_name,
    operator_id: item.operator_id,
    availableCount: item.charge_point_available_count,
    chargePointCount: item.charge_point_count,
    maxKwLabel: item.max_kw_label,
    typeLabel: item.type_label,
    priceLabel: item.price_label,
    connectors: item.connectors || [],
    currency: item.currency || { identifier: 'EUR' },
    theme,
    promotionCodeIds: item.promotion_code_ids,
    note: item.note ?? null,
  };
}

/**
 * Transform charge point details from API response (snake_case to camelCase)
 */
function transformChargePointDetails(item: any): ChargePointData {
  // Prefer the explicit API `state` field; the pin color is not a reliable proxy for
  // status (busy points use grey, passive uses blue, disconnected/error/maintenance all
  // use red). Fall back to pin-color inference only when the state is missing/unknown.
  const pinColor = item.pin?.color || 'gray';
  const chargePointState =
    mapApiStateToChargePointState(item.state ?? item.charge_point_state) ??
    inferStateFromPinColor(pinColor);

  // Parse theme data from details.theme (only available in details API, not map API)
  const theme = item.details?.theme
    ? {
        id: item.details.theme.id,
        name: item.details.theme.name,
        primaryColor: item.details.theme.primary_color,
        secondaryColor: item.details.theme.secondary_color,
        logoUrl: item.details.theme.logo_url,
      }
    : undefined;

  return {
    id: item.id,
    identifier: item.identifier,
    name: item.name,
    coordinate: parseLocation(
      item.location || `${item.lat || 0},${item.lng || 0}`
    ),
    address1: cleanText(item.address1),
    address2: cleanText(item.address2),
    address3: cleanText(item.address3),
    city: cleanText(item.city),
    zip: cleanText(item.zip),
    addressState: cleanText(item.address_state),
    maxKw: item.max_kw,
    maxKwLabel: item.max_kw_label,
    typeLabel: item.type_label,
    available: item.available ?? item.charge_point_available_count > 0,
    chargePointState,
    connectors: (item.connectors || []).map((c: any) => ({
      id: c.id,
      identifier: c.identifier,
      name: c.name,
      iconUrl: c.icon_url,
    })),
    currency: item.currency || { identifier: 'DKK' },
    stabilityScore: item.stability_score,
    pricingCollection: transformPricingCollection(
      item.pricings || item.pricing_collection
    ),
    operatorName: item.operator_name,
    chatActive: item.chat_active,
    smartQueueId: item.smart_queue_id,
    roaming: item.roaming,
    chargePointGroupId: item.charge_point_group_id,
    webAppUrl: item.details?.web_app_url,
    theme,
    pin: item.pin || {
      color: 'gray',
      type: 'single',
      number: 1,
      colors: undefined,
    },
    promotionCodeIds: item.promotion_code_ids,
    note: item.note ?? null,
  };
}

/**
 * Charge Points API service
 */
export const chargePointsApi = {
  /**
   * Get charge points for map viewport
   */
  async getChargePointsForMap(params: {
    bounds: LatLngBounds;
    center: LatLng;
    zoom: number;
    filters: ChargePointFilterParams;
    signal?: AbortSignal;
  }): Promise<ChargePointsMapResponse> {
    // Read segmented parameter from URL (?segmented=0 to disable, ?segmented=1 to enable)
    // Default is enabled (1)
    const urlParams = new URLSearchParams(window.location.search);
    const segmentedParam = urlParams.get('segmented') ?? '1';

    const queryParams = {
      top: params.bounds.north,
      bottom: params.bounds.south,
      left: params.bounds.west,
      right: params.bounds.east,
      // Reduce zoom by 1 for more aggressive clustering (min zoom 5)
      zoom: Math.max(5, params.zoom - 1),
      center_lat: params.center.lat,
      center_lng: params.center.lng,
      segmented: segmentedParam, // Enable/disable segmented colors for clusters via URL
      ...params.filters,
    };

    // Remove null/undefined values to avoid API validation errors
    const cleanParams = Object.fromEntries(
      Object.entries(queryParams).filter(
        ([_, value]) => value != null && value !== ''
      )
    );

    let response = await apiClient.get<any>(
      '/api/v1/charge_points/map',
      cleanParams,
      params.signal
    );

    // Few enough chargers to show them all? Ask for individual pins
    // (counts are documents: sites + charge points, like the page size)
    const inView =
      (response.single_list?.length ?? 0) +
      (response.cluster_list ?? []).reduce(
        (sum: number, c: any) => sum + (c.count ?? 0),
        0
      );
    if (
      breakOutEarly(urlParams) &&
      (response.cluster_list?.length ?? 0) > 0 &&
      // Leave clusters once this few chargers are in view (default 60): fits
      // the backend's 100-per-page individual search without pins piling up
      inView <= useClusterTuningStore.getState().breakout
    ) {
      response = await apiClient.get<any>(
        '/api/v1/charge_points/map',
        { ...cleanParams, zoom: BACKEND_CLUSTER_ZOOM },
        params.signal
      );
    }

    // Transform API response to expected format
    const single_list = (response.single_list || []).map(
      transformChargePointWrapper
    );
    const cluster_list = (response.cluster_list || []).map(transformCluster);

    return {
      single_list,
      cluster_list,
    };
  },

  /**
   * Get charge point details by ID
   */
  async getChargePointById(id: number): Promise<ChargePointDetailsResponse> {
    const response = await apiClient.get<any>(`/api/v1/charge_points/${id}`);
    return transformChargePointDetails(response);
  },

  /**
   * Get charge point site/group by ID
   */
  async getChargePointSiteById(
    siteId: number
  ): Promise<ChargePointGroupResponse> {
    const response = await apiClient.get<any>(
      `/api/v1/charge_point_groups/${siteId}`
    );
    return transformChargePointGroup(response);
  },

  /**
   * Get charge points in a group
   */
  async getChargePointGroupMembers(
    groupId: number
  ): Promise<ChargePointData[]> {
    const response = await apiClient.get<{ data: any[] }>(
      '/api/v1/charge_points',
      {
        charge_point_group_id: groupId,
      }
    );
    return response.data.map(transformChargePointDetails);
  },
};
